import { PlusIcon, SparkleIcon } from '@/assets/icons'
import { loadErrorMessage } from '@lib/apiError'
import { Button } from '@components/Button'
import EmptyState, { EmptyStateButton } from '@components/EmptyState'
import { ErrorNote } from '@components/Notice'
import { FillPageColumn, PageHeader, PageScroll } from '@components/Page'
import PageLoader from '@components/PageLoader'
import { useSocket } from '@context/SocketContext'
import { useOpenFromParam } from '@hooks/useOpenFromParam'
import { useEvents, useIdeas, useTravelSegments, useTrip, useTripDayInfos, useUpdateTripDayInfo } from '@hooks'
import type { ItineraryImportLocationState } from '@pages/ItineraryImport/paths'
import { editTripDestinationsPath } from '@pages/Trips/EditTripDestinationsFlow/paths'
import type { ItineraryImportSource } from '@orbit/api-types'
import { iconSizes } from '@/theme/tokens'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import EditDayInfoModal from './EditDayInfoModal'
import { useOpenEventCreate } from './EventCreateFlow'
import { editEventPath } from './EventEditFlow/paths'
import ImportStatusBanner from './ImportStatusBanner'
import ItineraryBoard from './ItineraryBoard'
import PickIdeaDialog from './PickIdeaDialog'
import { quickEditPath } from './QuickEdit/paths'
import { useEventMutations } from './useEventMutations'
import { useItineraryModel } from './useItineraryModel'
import { useSelectedDay } from './useSelectedDay'
import { TestIds } from '@/testIds'

const ItineraryPage: React.FC = () => {
	const { tripId = '' } = useParams<{ tripId: string }>()
	const navigate = useNavigate()
	const { data: trip, isLoading: tripLoading, error: tripError } = useTrip(tripId)
	const { data: eventsData, isLoading: eventsLoading, error: eventsError } = useEvents(tripId)
	const { data: dayInfosData } = useTripDayInfos(tripId)
	const { data: segmentsData } = useTravelSegments(tripId)
	const { data: ideasData } = useIdeas(tripId)
	const { isConnected, joinTrip, leaveTrip } = useSocket()
	const { changeEvent, removeEvent } = useEventMutations(tripId)
	const updateDayInfo = useUpdateTripDayInfo()

	const dayCount = trip?.dayCount ?? 0
	const { selectedDay, selectDay } = useSelectedDay(tripId, trip ? { startDate: trip.startDate, dayCount } : undefined)
	const openEventCreate = useOpenEventCreate(tripId, 'itinerary')
	/** The day an idea is being picked for, while the idea picker is up. */
	const [pickingIdeaFor, setPickingIdeaFor] = useState<number | null>(null)
	const [isDayInfoOpen, setIsDayInfoOpen] = useState(false)
	/** An activity a card in the Chat asked to open; `tick` changes per ask. */
	const [requestedEvent, setRequestedEvent] = useState<{ eventId: number; tick: number } | null>(null)
	useOpenFromParam('event', eventsLoading ? undefined : eventsData?.events, event => {
		selectDay(event.tripDayIndex)
		setRequestedEvent(previous => ({ eventId: event.id, tick: (previous?.tick ?? 0) + 1 }))
	})

	useEffect(() => {
		if (!tripId || !isConnected) return
		joinTrip(tripId)
		return () => leaveTrip(tripId)
	}, [tripId, isConnected, joinTrip, leaveTrip])

	const model = useItineraryModel({
		trip,
		events: eventsData?.events,
		dayInfos: dayInfosData?.tripDayInfo,
		segments: segmentsData?.segments,
		selectedDay,
	})
	const { eventsByDay, dayInfo, header, dayOptions } = model

	// -- Handlers ------------------------------------------------------------

	const openDestinationsEditor = () => navigate(editTripDestinationsPath(tripId))
	const openImport = (source: ItineraryImportSource) => {
		const state: ItineraryImportLocationState = { source }
		navigate(`/trips/${tripId}/import`, { state })
	}
	const openFullMap = (dayIndex: number | null) =>
		navigate(`/trips/${tripId}/map${dayIndex === null ? '' : `?day=${dayIndex + 1}`}`)

	const openQuickEdit = () => navigate(quickEditPath(tripId, selectedDay))
	const openAdd = () => openEventCreate(selectedDay)

	const handleSaveDayInfo = async (name: string | null, note: string | null) => {
		try {
			await updateDayInfo.mutateAsync({ tripId, dayIndex: selectedDay, data: { name, note } })
			setIsDayInfoOpen(false)
			toast.success('Day info updated')
		} catch {
			toast.error('Failed to save day info. Please try again.')
		}
	}

	// -- Render --------------------------------------------------------------

	if (tripLoading || eventsLoading) return <PageLoader label="Loading trip details…" />
	if (tripError || eventsError) {
		return <ErrorNote>{loadErrorMessage('trip', tripError ?? eventsError)}</ErrorNote>
	}
	if (!trip) return <ErrorNote>Trip not found</ErrorNote>

	const actions = (
		<>
			{dayCount > 0 && (
				<Button type="button" variant="secondary" onClick={openQuickEdit}>
					Quick Edit
				</Button>
			)}
			<Button type="button" variant="secondary" onClick={openDestinationsEditor}>
				Edit Destinations
			</Button>
			<Button type="button" variant="cool" onClick={() => openImport('itinerary_settings')} data-testid={TestIds.itinerary.aiImportButton}>
				<SparkleIcon sx={{ fontSize: iconSizes.sm }} />
				AI import
			</Button>
			{dayCount > 0 && (
				<Button type="button" onClick={openAdd} data-testid={TestIds.itinerary.addEventButton}>
					<PlusIcon sx={{ fontSize: iconSizes.sm }} />
					Add Event
				</Button>
			)}
		</>
	)

	return (
		<PageScroll>
			<FillPageColumn>
				<PageHeader title="Itinerary" backTo={{ path: `/trips/${tripId}`, label: trip.name }} actions={actions} />
				<ImportStatusBanner tripId={tripId} />
				{dayCount === 0 ? (
					<EmptyState
						art="itinerary"
						title="Set trip dates to begin planning"
						description="You can always change it later."
					>
						<EmptyStateButton onClick={openDestinationsEditor} data-testid={TestIds.itinerary.getStartedButton}>
							Set Trip Dates
						</EmptyStateButton>
						<EmptyStateButton variant="secondary" onClick={() => openImport('empty_state')}>
							Import Itinerary
						</EmptyStateButton>
					</EmptyState>
				) : (
					<ItineraryBoard
						model={model}
						onSelectDay={selectDay}
						onOpenFullMap={openFullMap}
						openEvent={requestedEvent}
						editing={{
							onEditDestinations: openDestinationsEditor,
							day: {
								onEditDayInfo: () => setIsDayInfoOpen(true),
								onAddEvent: openAdd,
								onImportIdea:
									(ideasData?.ideas.length ?? 0) > 0 ? () => setPickingIdeaFor(selectedDay) : undefined,
								onReorder: (eventId, eventIndex) => void changeEvent(eventId, { tripDayIndex: selectedDay, eventIndex }),
								onMoveToDay: (event, dayIndex) =>
									void changeEvent(
										event.id,
										{ tripDayIndex: dayIndex, eventIndex: eventsByDay.get(dayIndex)?.length ?? 0 },
										`'${event.name}' moved to ${dayOptions[dayIndex]?.label ?? `Day ${dayIndex + 1}`}`,
									),
								onEditEvent: event => navigate(editEventPath(tripId, event.id)),
								onDeleteEvent: event => void removeEvent(event.id, event.name),
							},
						}}
					/>
				)}
			</FillPageColumn>

			<PickIdeaDialog tripId={tripId} dayIndex={pickingIdeaFor} from="itinerary" onClose={() => setPickingIdeaFor(null)} />
			<EditDayInfoModal
				isOpen={isDayInfoOpen}
				onClose={() => setIsDayInfoOpen(false)}
				onSave={handleSaveDayInfo}
				isSaving={updateDayInfo.isPending}
				initialName={dayInfo?.name}
				initialNote={dayInfo?.note}
				dayLabel={header.subtitle ?? header.title}
			/>
		</PageScroll>
	)
}

export default ItineraryPage
